import { CaretRight, House } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

interface BreadcrumbItem {
  label: string;
  tab?: string;
  action?: () => void;
}

interface WorkflowBreadcrumbsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  selectedDatasetCount: number;
  appliedTemplate?: { name: string } | null;
  selectedSource?: { name: string } | null;
}

export function WorkflowBreadcrumbs({
  activeTab,
  onTabChange,
  selectedDatasetCount,
  appliedTemplate,
  selectedSource,
}: Readonly<WorkflowBreadcrumbsProps>) {
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'DataLab Explorer', tab: 'templates' },
    ];

    // Add template context if applied
    if (appliedTemplate) {
      breadcrumbs.push({
        label: `${appliedTemplate.name} Template`,
        tab: 'templates',
      });
    }

    // Add current step context
    switch (activeTab) {
      case 'templates':
        if (!appliedTemplate) {
          breadcrumbs.push({ label: 'Choose Starting Point' });
        }
        break;

      case 'discover':
        breadcrumbs.push({
          label: appliedTemplate
            ? 'Filtered Data Sources'
            : 'Browse Data Sources',
          tab: 'discover',
        });
        break;

      case 'datasets':
        breadcrumbs.push({ label: 'Data Sources', tab: 'discover' });
        if (selectedSource) {
          breadcrumbs.push({ label: selectedSource.name });
        }
        break;

      case 'browse-all':
        breadcrumbs.push({
          label: 'All Datasets',
          tab: 'browse-all',
        });
        break;

      case 'quality':
        breadcrumbs.push({
          label: 'Data Quality',
          tab: 'quality',
        });
        if (selectedDatasetCount > 0) {
          breadcrumbs.push({
            label: `${selectedDatasetCount} Dataset${selectedDatasetCount !== 1 ? 's' : ''}`,
          });
        }
        break;

      case 'correlations':
        breadcrumbs.push({
          label: 'Data Analysis',
          tab: 'correlations',
        });
        if (selectedDatasetCount >= 2) {
          breadcrumbs.push({
            label: 'Correlation Analysis',
          });
        }
        break;

      case 'transform':
        breadcrumbs.push({
          label: 'Data Transformation',
          tab: 'transform',
        });
        if (selectedDatasetCount > 0) {
          breadcrumbs.push({
            label: 'Pipeline Builder',
          });
        }
        break;

      case 'visualize':
        breadcrumbs.push({
          label: 'Data Visualization',
          tab: 'visualize',
        });
        break;

      case 'export':
        breadcrumbs.push({
          label: 'Export Data',
          tab: 'export',
        });
        break;

      default:
        break;
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className='flex items-center space-x-1 text-sm text-muted-foreground mb-4'>
      <House className='w-4 h-4' />

      {breadcrumbs.map((item, index) => (
        <div key={index} className='flex items-center'>
          {index > 0 && <CaretRight className='w-4 h-4 mx-1' />}

          {item.tab && index < breadcrumbs.length - 1 ? (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => onTabChange(item.tab!)}
              className='h-auto p-1 font-normal text-muted-foreground hover:text-foreground'
            >
              {item.label}
            </Button>
          ) : (
            <span
              className={
                index === breadcrumbs.length - 1
                  ? 'text-foreground font-medium'
                  : ''
              }
            >
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
}
